import React from 'react';
import { motion } from 'framer-motion';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleExclamation,
  faChartLine,
  faTrophy,
  faMapLocationDot
} from '@fortawesome/pro-light-svg-icons';

type ChatWelcomeProps = {
  onSuggestionClick: (prompt: string) => void;
  disabled?: boolean;
};

const suggestions: ReadonlyArray<{ title: string; prompt: string; icon: IconDefinition }> = [
  {
    title: 'Line items without spend',
    prompt: 'Show me active line items that should have actual spend but do not',
    icon: faCircleExclamation
  },
  {
    title: 'Active campaigns spend',
    prompt: 'Show me the top 10 active campaigns by actual spend over the last 30 days',
    icon: faChartLine
  },
  {
    title: 'Top campaigns',
    prompt: 'What are the top 10 campaigns by actual revenue?',
    icon: faTrophy
  },
  {
    title: 'Recent spend by region',
    prompt: 'Break down spend by region over the last 7 days',
    icon: faMapLocationDot
  }
];

export function ChatWelcome({ onSuggestionClick, disabled }: ChatWelcomeProps) {
  return (
    <div className="w-full max-w-2xl">
      <motion.div
        className="mb-6 text-center"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}>
        <h2 className="text-xl font-semibold">What would you like to explore?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Ask anything about your campaigns, line items, spend, and performance metrics. Or try one
          of these suggestions.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {suggestions.map((s, index) => (
          <motion.button
            key={s.title}
            type="button"
            disabled={disabled}
            onClick={() => onSuggestionClick(s.prompt)}
            className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-all hover:border-primary/50 hover:shadow-md disabled:pointer-events-none disabled:opacity-50"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <FontAwesomeIcon icon={s.icon} className="h-5 w-5" />
            </div>
            <div>
              <div className="font-medium">{s.title}</div>
              <div className="mt-0.5 text-sm text-muted-foreground">{s.prompt}</div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
